import { Body1 } from '@xo-union/tk-ui-typography';
import { FieldArray, Formik } from 'formik';
import honeybadger from 'honeybadger-js';
import React, { useContext } from 'react';
import * as Yup from 'yup';

import { Content, Footer, TripleAction } from '../../../libs/gs-component-right-rail';
import { Guest, Props as Shared } from '.';
import {
  Gds_CreateHouseholdInput,
  HouseholdQuery,
  useCreateHouseholdsMutation,
  useWeddingWebsiteQuery,
} from '../../../generated/graphql';
import useWeddingDayId from '../../../helpers/useWeddingDayId';
import { TrackingContext } from '../../../TrackingContext';
import { householdQuery } from '../../WeddingPageCard/RsvpInfo';
import shared from './addGuestEmail.scss';
import EditGuestItem from './EditGuestItem';
import styles from './addGuestEmail.scss';

interface Props extends Shared {
  guests: Guest[];
}

interface InitialValues {
  guests: Guest[];
}

const schema = Yup.object().shape({
  guests: Yup.array().of(
    Yup.object().shape({
      firstName: Yup.string().required('First Name is required'),
      lastName: Yup.string().nullable(),
      email: Yup.string().required(),
    })
  ),
});

const EditGuestForm: React.FC<Props> = ({ guests, onClose }) => {
  const { data: weddingData } = useWeddingWebsiteQuery();
  const [create, { loading }] = useCreateHouseholdsMutation();
  const { track } = useContext(TrackingContext);
  const eventId = useWeddingDayId();

  let initialValues: InitialValues = {
    guests,
  };

  const onAddGuest = async (values: InitialValues) => {
    const weddingId = weddingData?.wedding?.id!;

    try {
      const households: Gds_CreateHouseholdInput[] = values.guests.map(guest => {
        return {
          weddingId,
          people: [
            {
              isLeader: true,
              invitations: [
                {
                  eventId,
                },
              ],
              ...guest,
            },
          ],
        };
      });

      await create({
        variables: {
          input: households,
        },
        update(cache, { data }) {
          const { households } = cache.readQuery<HouseholdQuery>({ query: householdQuery })!;

          if (data?.createHouseholds) {
            cache.writeQuery({
              query: householdQuery,
              data: {
                households: [...households, ...data.createHouseholds],
              },
            });
          }
        },
      });

      track('WWS Share Interaction', { action: 'bulk add guests', guestAddedCount: households.length });

      households.forEach(_ => {
        track('Guest Added', { source: 'wws bulk add emails' });
      });
    } catch (error) {
      honeybadger.notify(error, { message: 'Error creatingHouseholds', context: { error } });
    }
    onClose();
  };

  return (
    <Formik initialValues={initialValues} validationSchema={schema} enableReinitialize onSubmit={onAddGuest}>
      {({ values, submitForm, isValid }) => (
        <>
          <Content className={styles.rightRailStyles}>
            <Body1>You can add their names here or do it later from your guest list.</Body1>
            <div className={shared.formContainer}>
              <FieldArray name="guests">
                {() => (
                  <>
                    {values.guests &&
                      values.guests.map((guest, index) => <EditGuestItem guest={guest} key={index} index={index} />)}
                  </>
                )}
              </FieldArray>
            </div>
          </Content>
          <Footer>
            <TripleAction
              primaryOnClick={submitForm}
              primaryDisabled={!isValid || loading}
              primaryText="Save and continue"
              secondaryOnClick={onClose}
              secondaryText="Cancel"
            />
          </Footer>
        </>
      )}
    </Formik>
  );
};
export default EditGuestForm;
